import { useCallback, useEffect, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import Header from "../components/Header";
import { api, API, formatDate, formatDateTime, formatEUR, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../i18n";
import { ArrowLeft, Loader2, RefreshCw, Maximize2, X } from "lucide-react";

const STATUS_OPTIONS = [
  { value: "pending", key: "status.pending", cls: "chip-pending" },
  { value: "processed", key: "status.processed", cls: "chip-processed" },
  { value: "error", key: "status.error", cls: "chip-error" },
];

const PAY_METHODS = ["numerario", "transferencia", "multibanco", "socio"];
const EMPTY_PAYMENT = { paid: false, method: "", date: "", note: "" };

export default function InvoiceDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useT();
  const [invoice, setInvoice] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [reextracting, setReextracting] = useState(false);
  const [error, setError] = useState("");
  const [zoom, setZoom] = useState(false);
  const [payment, setPayment] = useState(EMPTY_PAYMENT);

  const load = useCallback(async () => {
    const { data } = await api.get(`/invoices/${id}`);
    setInvoice(data);
    setNotes(data.notes || "");
    setPayment(data.payment || EMPTY_PAYMENT);
  }, [id]);

  useEffect(() => {
    load().catch((e) => setError(formatApiError(e.response?.data?.detail) || e.message));
  }, [load]);

  const invoiceId = invoice?.id;
  useEffect(() => {
    if (!invoiceId) return;
    let revoked = null;
    (async () => {
      try {
        const resp = await api.get(`/invoices/${invoiceId}/image`, { responseType: "blob" });
        const url = URL.createObjectURL(resp.data);
        revoked = url;
        setImageUrl(url);
      } catch (e) {
        console.error("Falha ao carregar imagem da fatura:", e);
      }
    })();
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [invoiceId]);

  const location = useLocation();
  const isAccountant = user?.role === "accountant" && !location.pathname.startsWith("/cliente");

  const setStatus = async (status) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/invoices/${id}`, { status });
      setInvoice(data);
    } finally { setSaving(false); }
  };

  const saveNotes = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/invoices/${id}`, { notes });
      setInvoice(data);
    } finally { setSaving(false); }
  };

  const savePayment = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/invoices/${id}`, { payment });
      setInvoice(data);
      setPayment(data.payment || EMPTY_PAYMENT);
    } finally { setSaving(false); }
  };

  const reextract = async () => {
    setReextracting(true);
    try {
      await api.post(`/invoices/${id}/reextract`);
      await load();
    } finally { setReextracting(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen paper-grain">
        <Header />
        <div className="max-w-3xl mx-auto p-6 text-[color:var(--ink-muted)]">{error}</div>
      </div>
    );
  }
  if (!invoice) {
    return (
      <div className="min-h-screen paper-grain">
        <Header />
        <div className="max-w-3xl mx-auto p-6 text-[color:var(--ink-muted)]">{t("common.loading")}</div>
      </div>
    );
  }

  const ef = invoice.extracted_fields || {};
  const status = STATUS_OPTIONS.find((s) => s.value === invoice.status) || STATUS_OPTIONS[0];

  return (
    <div className="paper-grain min-h-screen">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="invoice-detail-page">
        <Link to={isAccountant ? "/contabilista" : "/cliente"} className="inline-flex items-center gap-2 text-sm text-[color:var(--ink-muted)] hover:text-[color:var(--ink)]">
          <ArrowLeft size={16} /> {t("common.back")}
        </Link>

        <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
          <div>
            <span className="label-eyebrow">{t("id.eyebrow")}</span>
            <h1 className="font-display text-2xl sm:text-3xl mt-1" data-testid="invoice-vendor-title">{ef.vendor || t("common.vendor_unknown")}</h1>
            <div className="text-sm text-[color:var(--ink-muted)] mt-1">{t("id.received")} {formatDateTime(invoice.created_at)} · {invoice.client_name}</div>
          </div>
          <span className={`chip ${status.cls}`} data-testid="invoice-status-chip">{t(status.key)}</span>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mt-8">
          <div className="card-soft p-0 overflow-hidden lg:order-2 relative group" data-testid="invoice-image-card">
            {imageUrl ? (
              <>
                <img src={imageUrl} alt="Fatura" className="w-full object-contain bg-[color:var(--paper)] max-h-[640px]" />
                <button
                  onClick={() => setZoom(true)}
                  className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-lg bg-[color:var(--ink)]/80 text-white text-xs px-3 py-1.5 backdrop-blur hover:bg-[color:var(--ink)] transition-colors"
                  data-testid="expand-image-btn"
                >
                  <Maximize2 size={14} /> Ampliar
                </button>
              </>
            ) : (
              <div className="p-16 text-center text-[color:var(--ink-muted)]">{t("id.loading_image")}</div>
            )}
          </div>

          <div className="space-y-6 lg:order-1">
            <div className="card-soft" data-testid="invoice-data-card">
              <div className="flex items-center justify-between">
                <span className="label-eyebrow">{t("id.ai_data")}</span>
                {isAccountant && (
                  <button onClick={reextract} disabled={reextracting} className="text-xs inline-flex items-center gap-1 text-[color:var(--primary)] hover:underline" data-testid="reextract-btn">
                    {reextracting ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                    {t("id.reextract")}
                  </button>
                )}
              </div>
              <dl className="mt-4 grid grid-cols-2 gap-y-3 gap-x-6 font-mono-data text-sm">
                <Row label={t("id.vendor")} value={ef.vendor} />
                <Row label="NIF" value={ef.nif} />
                <Row label={t("id.invoice_number")} value={ef.invoice_number} />
                <Row label={t("id.invoice_date")} value={ef.date && formatDate(ef.date)} />
                <Row label={t("id.total")} value={formatEUR(ef.total)} />
                <Row label={t("id.currency")} value={ef.currency || "EUR"} />
              </dl>
              {ef.error && (
                <div className="text-xs text-[#991B1B] bg-[#FEE2E2] border border-[#FECACA] rounded-lg p-3 mt-4">
                  {t("id.extract_error")} {ef.error}
                </div>
              )}
            </div>

            {/* Payment info */}
            {isAccountant ? (
              <div className="card-soft" data-testid="payment-readonly-card">
                <span className="label-eyebrow">{t("pay.title")}</span>
                {invoice.payment && (invoice.payment.paid || invoice.payment.method || invoice.payment.date || invoice.payment.note) ? (
                  <dl className="mt-3 grid grid-cols-2 gap-y-3 gap-x-6 font-mono-data text-sm">
                    <Row label={t("pay.paid")} value={invoice.payment.paid ? "Sim" : t("pay.not_paid")} />
                    <Row label={t("pay.method")} value={invoice.payment.method ? t(`pay.m.${invoice.payment.method}`) : null} />
                    <Row label={t("pay.date")} value={invoice.payment.date && formatDate(invoice.payment.date)} />
                    <Row label={t("pay.note")} value={invoice.payment.note} />
                  </dl>
                ) : (
                  <p className="mt-3 text-sm text-[color:var(--ink-muted)]">{t("pay.none")}</p>
                )}
              </div>
            ) : (
              <div className="card-soft" data-testid="payment-edit-card">
                <span className="label-eyebrow">{t("pay.title")}</span>
                <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer" data-testid="payment-paid-toggle">
                  <input type="checkbox" className="w-4 h-4 accent-[color:var(--primary)]" checked={!!payment.paid} onChange={(e) => setPayment({ ...payment, paid: e.target.checked })} />
                  {t("pay.paid")}
                </label>
                <div className="grid sm:grid-cols-2 gap-3 mt-4">
                  <div>
                    <label className="label-eyebrow block mb-1">{t("pay.method")}</label>
                    <select className="input-soft py-2 text-sm" value={payment.method || ""} onChange={(e) => setPayment({ ...payment, method: e.target.value })} data-testid="payment-method-select">
                      <option value="">—</option>
                      {PAY_METHODS.map((m) => <option key={m} value={m}>{t(`pay.m.${m}`)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="label-eyebrow block mb-1">{t("pay.date")}</label>
                    <input type="date" className="input-soft py-2 text-sm" value={payment.date || ""} onChange={(e) => setPayment({ ...payment, date: e.target.value })} data-testid="payment-date-input" />
                  </div>
                </div>
                <div className="mt-3">
                  <label className="label-eyebrow block mb-1">{t("pay.note")}</label>
                  <textarea rows={2} className="input-soft resize-none text-sm" placeholder={t("pay.note_ph")} value={payment.note || ""} onChange={(e) => setPayment({ ...payment, note: e.target.value })} data-testid="payment-note-input" />
                </div>
                <button onClick={savePayment} disabled={saving} className="btn-primary mt-3 text-sm" data-testid="save-payment-btn">
                  {saving ? t("common.saving") : t("pay.save")}
                </button>
              </div>
            )}

            {isAccountant && (
              <div className="card-soft" data-testid="accountant-controls">
                <span className="label-eyebrow">{t("id.control")}</span>
                <div className="mt-3 flex flex-wrap gap-2">
                  {STATUS_OPTIONS.map((s) => (
                    <button
                      key={s.value}
                      onClick={() => setStatus(s.value)}
                      disabled={saving}
                      className={`chip ${s.cls} ${invoice.status === s.value ? "ring-2 ring-[color:var(--primary)]" : "opacity-60 hover:opacity-100"}`}
                      data-testid={`set-status-${s.value}`}
                    >{t(s.key)}</button>
                  ))}
                </div>
                <div className="mt-5">
                  <label className="label-eyebrow block mb-2">{t("id.internal_notes")}</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={4}
                    className="input-soft resize-none"
                    data-testid="notes-input"
                    placeholder={t("id.notes_placeholder")}
                  />
                  <button onClick={saveNotes} disabled={saving} className="btn-primary mt-3 text-sm" data-testid="save-notes-btn">
                    {saving ? t("common.saving") : t("id.save_notes")}
                  </button>
                </div>
              </div>
            )}

            {!isAccountant && invoice.notes && (
              <div className="card-soft" data-testid="client-notes-card">
                <span className="label-eyebrow">{t("id.accountant_notes")}</span>
                <p className="mt-3 text-sm whitespace-pre-wrap">{invoice.notes}</p>
              </div>
            )}
          </div>
        </div>
      </main>

      {zoom && imageUrl && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 fade-in" onClick={() => setZoom(false)} data-testid="image-zoom-modal">
          <button
            onClick={() => setZoom(false)}
            className="absolute top-4 right-4 inline-flex items-center gap-1 rounded-lg bg-white/10 text-white text-sm px-3 py-2 hover:bg-white/20 transition-colors"
            data-testid="close-zoom-btn"
          >
            <X size={18} /> Fechar
          </button>
          <img
            src={imageUrl}
            alt="Fatura ampliada"
            className="max-w-full max-h-[92vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <>
      <dt className="text-[color:var(--ink-muted)] uppercase tracking-wider text-xs">{label}</dt>
      <dd className="text-right">{value || "—"}</dd>
    </>
  );
}
